import React, { useEffect, useState } from 'react';
import { ChevronLeft, Plus, Pencil, Trash2, X as XIcon, Megaphone, UserPlus } from 'lucide-react';
import { useSponsors } from '../store/sponsors';
import { Sponsor } from '../types';
import { Field, Input, PrimaryBtn, SecondaryBtn, ErrorMsg, Spinner, useConfirm } from '../components/UI';
import { FYLKER, KOMMUNER_BY_FYLKE } from '../lib/norwayGeo';

const TAGLINE_MAXLEN = 85;

type FormState = {
  company_name: string;
  tagline: string;
  image_url: string;
  link_url: string;
  contact_email: string;
  price_nok: string;
  start_date: string;
  end_date: string;
  active: boolean;
  target_fylker: string[];
  target_kommuner: string[];
};

const emptyForm: FormState = {
  company_name: '', tagline: '', image_url: '', link_url: '', contact_email: '', price_nok: '',
  start_date: new Date().toISOString().slice(0, 10), end_date: '', active: true,
  target_fylker: [], target_kommuner: [],
};

const toForm = (s: Sponsor): FormState => ({
  company_name: s.company_name,
  tagline: s.tagline || '',
  image_url: s.image_url || '',
  link_url: s.link_url || '',
  contact_email: s.contact_email || '',
  price_nok: s.price_nok != null ? String(s.price_nok) : '',
  start_date: s.start_date,
  end_date: s.end_date,
  active: s.active,
  target_fylker: s.target_fylker || [],
  target_kommuner: s.target_kommuner || [],
});

const scopeLabel = (s: Sponsor): string => {
  if (s.target_kommuner?.length) return `Kommune: ${s.target_kommuner.join(', ')}`;
  if (s.target_fylker?.length) return `Fylke: ${s.target_fylker.join(', ')}`;
  return 'Hele Norge';
};

const addMonths = (dateStr: string, months: number) => {
  const d = new Date(dateStr + 'T00:00:00');
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
};

const statusOf = (s: Sponsor): { label: string; color: string } => {
  const today = new Date().toISOString().slice(0, 10);
  if (!s.active) return { label: 'Inaktiv', color: 'var(--text3)' };
  if (s.start_date > today) return { label: 'Planlagt', color: 'var(--blue)' };
  if (s.end_date < today) return { label: 'Utløpt', color: 'var(--red)' };
  return { label: 'Aktiv', color: 'var(--green)' };
};

export const SponsorDashPage: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const {
    allSponsors, fetchAllSponsorsForAdmin, createSponsor, updateSponsor, deleteSponsor,
    clickCounts, fetchClickCounts,
    sponsorAdminsList, fetchSponsorAdminsList, addSponsorAdmin, removeSponsorAdmin,
  } = useSponsors();

  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [durationMonths, setDurationMonths] = useState<number | null>(null);
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);

  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [adminError, setAdminError] = useState('');
  const [addingAdmin, setAddingAdmin] = useState(false);

  const { confirm, dialog } = useConfirm();

  useEffect(() => {
    Promise.all([fetchAllSponsorsForAdmin(), fetchClickCounts(), fetchSponsorAdminsList()])
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startEdit = (s: Sponsor) => { setEditingId(s.id); setCreating(false); setForm(toForm(s)); setDurationMonths(null); setSaveError(''); };
  const startCreate = () => { setCreating(true); setEditingId(null); setForm(emptyForm); setDurationMonths(null); setSaveError(''); };
  const cancelEdit = () => { setEditingId(null); setCreating(false); setSaveError(''); };

  const handleSave = async () => {
    if (!form.company_name.trim()) { setSaveError('Firmanavn er påkrevd'); return; }
    if (!form.start_date || !form.end_date) { setSaveError('Start- og sluttdato er påkrevd'); return; }
    setSaving(true); setSaveError('');
    const payload: Partial<Sponsor> = {
      company_name: form.company_name.trim(),
      tagline: form.tagline.trim() || null,
      image_url: form.image_url.trim() || null,
      link_url: form.link_url.trim() || null,
      contact_email: form.contact_email.trim() || null,
      price_nok: form.price_nok.trim() ? Number(form.price_nok) : null,
      start_date: form.start_date,
      end_date: form.end_date,
      active: form.active,
      // Har man plukket spesifikke kommuner, snevres målgruppen inn til akkurat
      // dem (fylkene brukes da kun som filter for hvilke kommuner som var
      // valgbare, ikke som en egen bredere målgruppe i tillegg).
      target_fylker: form.target_kommuner.length ? [] : form.target_fylker,
      target_kommuner: form.target_kommuner,
    };
    const err = creating ? await createSponsor(payload) : await updateSponsor(editingId!, payload);
    setSaving(false);
    if (err) { setSaveError(err); return; }
    setEditingId(null); setCreating(false);
  };

  const handleDelete = async (s: Sponsor) => {
    const ok = await confirm(`Slette «${s.company_name}»? Dette kan ikke angres.`, { confirmLabel: 'Slett', danger: true });
    if (!ok) return;
    await deleteSponsor(s.id);
  };

  const handleAddAdmin = async () => {
    if (!newAdminEmail.trim()) return;
    setAddingAdmin(true); setAdminError('');
    const err = await addSponsorAdmin(newAdminEmail.trim());
    setAddingAdmin(false);
    if (err) { setAdminError(err); return; }
    setNewAdminEmail('');
  };

  const handleRemoveAdmin = async (userId: string, email: string) => {
    const ok = await confirm(`Fjerne tilgang for ${email}?`, { confirmLabel: 'Fjern', danger: true });
    if (!ok) return;
    await removeSponsorAdmin(userId);
  };

  return (
    <div style={{ position: 'absolute', inset: 0, background: 'var(--surface-solid)', zIndex: 100, display: 'flex', flexDirection: 'column', overflowY: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: 'calc(var(--safe-top) + 14px) 16px 14px', background: 'var(--surface)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px 2px 0', color: 'var(--text)', display: 'flex', alignItems: 'center' }}>
          <ChevronLeft size={24} strokeWidth={2} />
        </button>
        <div>
          <div style={{ fontSize: 17, fontWeight: 700 }}>Sponsor-dashbord</div>
          <div style={{ fontSize: 12, color: 'var(--text3)' }}>Administrer annonser og tilgang</div>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' as any, padding: 16 }}>
        {loading ? <Spinner /> : (
          <>
            {(creating || editingId) ? (
              <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: 14, marginBottom: 20 }}>
                <Field label="Firmanavn">
                  <Input value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} placeholder="F.eks. Byggern AS" />
                </Field>
                <Field label={`Tekstlinje (${form.tagline.length}/${TAGLINE_MAXLEN} tegn)`}>
                  <Input
                    value={form.tagline}
                    maxLength={TAGLINE_MAXLEN}
                    onChange={e => setForm(f => ({ ...f, tagline: e.target.value }))}
                    placeholder="F.eks. 10% rabatt for Toolshare-brukere"
                  />
                </Field>
                <Field label="Bilde-URL (valgfritt)">
                  <Input value={form.image_url} onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))} placeholder="https://..." />
                </Field>
                <Field label="Lenke-URL (valgfritt)">
                  <Input value={form.link_url} onChange={e => setForm(f => ({ ...f, link_url: e.target.value }))} placeholder="https://..." />
                </Field>
                <Field label="Kontakt-e-post (valgfritt, kun til intern bruk)">
                  <Input value={form.contact_email} onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))} placeholder="kontakt@firma.no" />
                </Field>
                <Field label="Pris i kr (valgfritt, kun til intern bruk)">
                  <Input type="number" value={form.price_nok} onChange={e => setForm(f => ({ ...f, price_nok: e.target.value }))} placeholder="500" />
                </Field>
                <Field label="Startdato">
                  <Input
                    type="date"
                    value={form.start_date}
                    onChange={e => {
                      const start_date = e.target.value;
                      setForm(f => ({ ...f, start_date, end_date: durationMonths ? addMonths(start_date, durationMonths) : f.end_date }));
                    }}
                  />
                </Field>
                <Field label="Varighet (valgfritt, fyller ut sluttdato automatisk)">
                  <div style={{ display: 'flex', gap: 6 }}>
                    {[3, 6, 9, 12].map(m => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => { setDurationMonths(m); setForm(f => ({ ...f, end_date: addMonths(f.start_date, m) })); }}
                        style={{
                          flex: 1, padding: '9px 4px', borderRadius: 'var(--radius-sm)',
                          border: `1px solid ${durationMonths === m ? 'var(--green)' : 'var(--border-strong)'}`,
                          background: durationMonths === m ? 'var(--green)' : 'none',
                          fontSize: 14, fontWeight: 600, color: durationMonths === m ? '#fff' : 'var(--text2)', cursor: 'pointer',
                        }}
                      >
                        {m} mnd
                      </button>
                    ))}
                  </div>
                </Field>
                <Field label="Sluttdato">
                  <Input
                    type="date"
                    value={form.end_date}
                    onChange={e => { setDurationMonths(null); setForm(f => ({ ...f, end_date: e.target.value })); }}
                  />
                </Field>
                <Field label="Målgruppe: fylke (tomt = hele Norge)">
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {FYLKER.map(f => {
                      const active = form.target_fylker.includes(f);
                      return (
                        <button
                          key={f}
                          type="button"
                          onClick={() => setForm(prev => {
                            const target_fylker = active ? prev.target_fylker.filter(x => x !== f) : [...prev.target_fylker, f];
                            const allowedKommuner = new Set(target_fylker.flatMap(fy => KOMMUNER_BY_FYLKE[fy] || []));
                            return { ...prev, target_fylker, target_kommuner: prev.target_kommuner.filter(k => allowedKommuner.has(k)) };
                          })}
                          style={{
                            padding: '6px 11px', borderRadius: 30,
                            border: `1px solid ${active ? 'var(--green)' : 'var(--border-strong)'}`,
                            background: active ? 'var(--green-light)' : 'none',
                            color: active ? 'var(--green-dark)' : 'var(--text2)',
                            fontSize: 13, fontWeight: 500, cursor: 'pointer',
                          }}
                        >
                          {f}
                        </button>
                      );
                    })}
                  </div>
                </Field>

                {form.target_fylker.length > 0 && (
                  <Field label="Målgruppe: kommune (tomt = hele det valgte fylket)">
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {form.target_fylker.flatMap(f => KOMMUNER_BY_FYLKE[f] || []).map(k => {
                        const active = form.target_kommuner.includes(k);
                        return (
                          <button
                            key={k}
                            type="button"
                            onClick={() => setForm(prev => ({
                              ...prev,
                              target_kommuner: active ? prev.target_kommuner.filter(x => x !== k) : [...prev.target_kommuner, k],
                            }))}
                            style={{
                              padding: '6px 11px', borderRadius: 30,
                              border: `1px solid ${active ? 'var(--green)' : 'var(--border-strong)'}`,
                              background: active ? 'var(--green-light)' : 'none',
                              color: active ? 'var(--green-dark)' : 'var(--text2)',
                              fontSize: 13, fontWeight: 500, cursor: 'pointer',
                            }}
                          >
                            {k}
                          </button>
                        );
                      })}
                    </div>
                  </Field>
                )}

                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, marginBottom: 14, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} />
                  Aktiv
                </label>
                {saveError && <ErrorMsg msg={saveError} />}
                <PrimaryBtn onClick={handleSave} disabled={saving}>{saving ? 'Lagrer...' : 'Lagre'}</PrimaryBtn>
                <SecondaryBtn onClick={cancelEdit}>Avbryt</SecondaryBtn>
              </div>
            ) : (
              <button
                onClick={startCreate}
                style={{ width: '100%', padding: '11px 14px', marginBottom: 16, borderRadius: 'var(--radius-sm)', border: '1px dashed var(--border-strong)', background: 'none', fontSize: 14, fontWeight: 500, color: 'var(--text2)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                <Plus size={15} strokeWidth={2} /> Ny sponsor
              </button>
            )}

            {allSponsors.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '16px 0', color: 'var(--text3)', fontSize: 14 }}>Ingen sponsorer ennå</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 28 }}>
                {allSponsors.map(s => {
                  const status = statusOf(s);
                  return (
                    <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', background: 'var(--surface)' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.company_name}</span>
                          <span style={{ fontSize: 11, fontWeight: 700, color: status.color, flexShrink: 0 }}>{status.label}</span>
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {clickCounts[s.id] ?? 0} klikk · {s.start_date} – {s.end_date} · {scopeLabel(s)}
                        </div>
                      </div>
                      <button
                        onClick={() => updateSponsor(s.id, { active: !s.active })}
                        title={s.active ? 'Deaktiver' : 'Aktiver'}
                        style={{ width: 40, height: 22, borderRadius: 12, background: s.active ? 'var(--green)' : 'var(--border-strong)', border: 'none', cursor: 'pointer', position: 'relative', flexShrink: 0, padding: 0 }}
                      >
                        <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: s.active ? 20 : 2, transition: 'left 0.15s' }} />
                      </button>
                      <button onClick={() => startEdit(s)} title="Endre"
                        style={{ padding: '5px 8px', borderRadius: 30, border: '1px solid var(--border-strong)', background: 'none', color: 'var(--text2)', cursor: 'pointer', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        <Pencil size={13} strokeWidth={2} />
                      </button>
                      <button onClick={() => handleDelete(s)} title="Slett"
                        style={{ padding: '5px 8px', borderRadius: 30, border: '1px solid #fca5a5', background: 'none', color: 'var(--red)', cursor: 'pointer', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        <Trash2 size={13} strokeWidth={2} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Tilgangsstyring */}
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <Megaphone size={16} color="var(--text2)" strokeWidth={1.8} />
                <span style={{ fontSize: 14, fontWeight: 700 }}>Hvem har tilgang til dette dashbordet</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
                {sponsorAdminsList.map(a => (
                  <div key={a.user_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', background: 'var(--bg)' }}>
                    <span style={{ flex: 1, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.email}</span>
                    <button onClick={() => handleRemoveAdmin(a.user_id, a.email)} title="Fjern tilgang"
                      style={{ padding: '4px 7px', borderRadius: 30, border: '1px solid #fca5a5', background: 'none', color: 'var(--red)', cursor: 'pointer', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                      <XIcon size={12} strokeWidth={2.5} />
                    </button>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <Input
                  type="email"
                  value={newAdminEmail}
                  onChange={e => setNewAdminEmail(e.target.value)}
                  placeholder="E-postadresse"
                  style={{ flex: 1 }}
                />
                <button onClick={handleAddAdmin} disabled={addingAdmin}
                  style={{ padding: '0 16px', borderRadius: 'var(--radius-sm)', border: 'none', background: 'var(--green)', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                  <UserPlus size={15} strokeWidth={2} /> Legg til
                </button>
              </div>
              {adminError && <div style={{ marginTop: 10 }}><ErrorMsg msg={adminError} /></div>}
            </div>
          </>
        )}
      </div>
      {dialog}
    </div>
  );
};
